/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Field } from "formik";
import { isEmpty, every, isString, find, isObject, includes, isEqual, map, head } from "lodash";
import moment from "moment";
import { Icon, Tooltip } from "@material-ui/core";

import LoadingState from "components/items/loading-state";
import EmptyState from "components/items/empty-state";
import Form from "components/form";
import { showMessage } from "app/store/actions/message.actions";
import { getDefaultRoute } from "utils/get-environment-variables";

import { getAllCodeSetValues } from "app/main/codeSet/reducers/codeSet.reducers";
import { getReferralFormLayoutSetting,
  getPrimaryPatientIdentifier,
  getAllowReassignSpecialtyStates,
  getReferralWorklistSettings,
  getEnableStreamlinedAddReferral,
  getSpecialtyLabel,
  getSpecialtyFormProfileLabel,
  getReferralSourceDocumentUploadDisabledMarkdown,
  getShowReferralSourceDocumentUploadDisableIcon,
  getTotalAttachmentSizeLimitBytes } from "app/auth/store/reducers/system-configuration";

import { createManualReferral,
  editManualReferral,
  clearReferralSourceDocumentFiles,
  getReferralSourceDocumentFilePreviewData,
  draftManualReferral } from "app/main/referrals/actions/referrals.actions";
import { getDefaultReferralSourceDocumentFile, isCurrentReferralSourceDocumentFileLoading } from "app/main/referrals/reducers/referrals.reducers";

import { fetchPatientPresentingComplaints } from "app/main/referrals/actions/patientReferrals.actions";
import { getPatientPresentingComplaints } from "app/main/referrals/reducers/patientReferrals.reducers";
import { getSignedInOrgUnit } from "app/auth/store/reducers/user.reducer";

import DefaultButton from "components/items/default-button";
import IconButton from "components/items/icon-button";
import Section from "components/items/section";
import { resetCurrentAttachment } from "app/main/patients/actions/patientOCR.selector.actions";
import ReferralWarningMessage from "app/main/referrals/components/referral-warning-message-alert";
import { resetPatientSearch } from "app/main/patients/actions/patientsSearch.actions";

import { openDialog } from "app/store/actions/dialog.actions";
import CreateReferralForm from "app/main/referrals/components/create-referral-form";
import BackReferralConfirmDialog from "app/main/referrals/components/add-referral-form/back-referral-confirm-dialog";

import { clearUploadAttachment } from "app/main/referralDocumentAttachmentUploads/actions/referralDocumentAttachmentUploads.actions";
import ReferralDocumentAttachmentUpload from "app/main/referralDocumentAttachmentUploads/components/referral-document-attachment-upload";

import { normalizePatient } from "app/main/patients/reducers/patients.reducers";
import withPermissions from "permissions/withPermissions";
import ReferralSourceDocumentPreview from "./referralSourceDocumentPreview";
import ReferralSourceDocumentDisableUpload from "./referralSourceDocumentDisableUpload";
import ReferralFormComponent from "../referral-form-component";
import PatientSummaryCard from "../patient-summary-card";
import SubmitButton from "../submit-button";
import ReferrerInformationSection from "./referrer-information-section";
import ReferralInformationSection from "./referral-information-section";
import BillingInformationSection from "./billing-information-section";
import AcknowledgementSection from "./acknowledgement-section";

const useStyles = makeStyles(theme => ({
  rootContainer: {
    flexDirection: "column",
    display: "flex",
    minHeight: "100%",
  },
  headerContainer: {
    display: "flex",
    backgroundColor: theme.palette.background.default,
    alignItems: "center",
    marginTop: theme.spacing(1),
    borderBottom: "1px solid #e0e0e0",
  },
  documentContainer: {
    flexGrow: "1",
    overflowY: "auto",
  },
}));

const CreateReferralFormComponent = ({
  orgUnitId,
  currentReferral,
  schema,
  fieldLabels,
  fieldSettings,
  loadingFieldSettings,
  autoReferralFullName,
  autoReferringOrgUnitId,
  templateReferral,
  currentAssignedToSpecialty,
  setCurrentAssignSpecialty,
  enableDraft,
  disableSourceDocumentUpload,
  renderTemplateComponents,

  onSucceed,
  patient: presetPatient,
  sourceDocument,
  isEdit,
  allowSaveAsDraft,
  warningMessages,
  onCancel,
  isFormStreamlinedAddReferral = false,
  isFromNewPatient = false,
  enableBack = false,
  presentingComplaintMaxLength,
  hasPermissionReferralsCreate,
  hasPermissionReferralsDraft,
  hasPermissionReferralsEdit,
  ...other
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const codeSetValues = useSelector(getAllCodeSetValues);
  const codeSetValueProperties = codeSetValueId => find(codeSetValues, x => x.id === codeSetValueId)?.propertiesJson;

  const referralSourceDocumentFile = useSelector(getDefaultReferralSourceDocumentFile);
  const [previewSourceDocument, setPreviewSourceDocument] = useState(sourceDocument);
  const isReferralSourceDocumentFileLoading = useSelector(isCurrentReferralSourceDocumentFileLoading);

  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [saveAsProcess, setSaveAsProcess] = useState(false);
  // allow reassignment when
  // 1. is new form
  // 2. when current referral status in allowed states
  const AllowReassignSpecialtyStates = useSelector(getAllowReassignSpecialtyStates);
  const allowSpecialtyReassignment = !isEdit || includes(AllowReassignSpecialtyStates, currentReferral?.referralStatus);
  const primaryPatientIdentifier = useSelector(getPrimaryPatientIdentifier);

  // consider 2 way of patient details
  // 1. from new patient or existing - presetPatient
  // 2. from edit - currentReferral.patient
  const patientDetails = useMemo(() => presetPatient ?? currentReferral?.patient, [presetPatient, currentReferral?.patient]);
  const referralWorklistSettings = useSelector(getReferralWorklistSettings);
  const isEnablePresentingComplaintWarning = !isEmpty(referralWorklistSettings?.patientPresentingComplaintWarningMessage);
  const patientPresentingComplaints = useSelector(state => getPatientPresentingComplaints(state, patientDetails?.patientId));

  const orgUnit = useSelector(getSignedInOrgUnit);
  const enableStreamlinedAddReferral = useSelector(getEnableStreamlinedAddReferral);
  const referralFormLayoutSetting = useSelector(getReferralFormLayoutSetting);
  const [isNewUpload, setIsNewUpload] = useState(isEdit !== true);
  const currentAssignedTo = currentReferral?.assignedTo ? { value: currentReferral?.assignedTo?.assignedToSpecialtyId, label: currentReferral?.assignedTo?.assignedToSpecialtyName } : null;
  const referredToSpecialty = sourceDocument?.referredToSpecialty ?? currentAssignedTo;
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [isReading] = useState(false);
  const isEmptyPatient = isEmpty(patientDetails) || (isEdit && isEmpty(patientDetails?.patientId));
  const multiplePresentingComplaint = presentingComplaintMaxLength > 1;

  const specialtyLabel = useSelector(getSpecialtyLabel);
  const specialtyFormProfileLabel = useSelector(getSpecialtyFormProfileLabel);
  const currentReferralOrgUnitId = currentReferral?.orgUnitId ?? orgUnit?.id;
  const referralSourceDocumentUploadDisabledMarkdown = useSelector(getReferralSourceDocumentUploadDisabledMarkdown);
  const showReferralSourceDocumentUploadDisableIcon = useSelector(getShowReferralSourceDocumentUploadDisableIcon);
  const [showAcknowledgementSection, setShowAcknowledgementSection] = useState(false);
  const totalAttachmentSizeLimitBytes = useSelector(getTotalAttachmentSizeLimitBytes);

  useEffect(() => {
    if (currentReferralOrgUnitId && currentReferral?.referralSourceDocument) {
      // fetch current source document preview data
      dispatch(getReferralSourceDocumentFilePreviewData(currentReferralOrgUnitId, currentReferral.referralSourceDocument.id, currentReferral.referralSourceDocument.fileId));
    }
  }, [currentReferralOrgUnitId, currentReferral?.referralSourceDocument]);

  useEffect(() => {
    if (currentReferralOrgUnitId && patientDetails?.patientId && isEnablePresentingComplaintWarning) {
      dispatch(fetchPatientPresentingComplaints(currentReferralOrgUnitId, patientDetails?.patientId));
    }
  }, [currentReferralOrgUnitId, patientDetails?.patientId]);

  useEffect(() => {
    setPreviewSourceDocument(referralSourceDocumentFile ?? sourceDocument);
  }, [referralSourceDocumentFile]);

  useEffect(() => () => dispatch(clearReferralSourceDocumentFiles()), []);

  // Determine if the "Save as Draft" button should be shown
  // eslint-disable-next-line arrow-body-style
  const useSaveAsDraftButton = (enabled, allowed, status, permissionReferralsDraft) => {
    return React.useMemo(() => {
      if (!enabled) return { show: false };

      if (!permissionReferralsDraft) return { show: false };

      if (allowed) return { show: true };

      if (status === "Draft") return { show: true };

      return { show: false };
    }, [enabled, allowed, status, permissionReferralsDraft]);
  };

  // We only want to show this tip to users when the Save as Draft feature is disabled for this form profile
  const useTipSaveAsDraftButton = enabled => React.useMemo(() => {
    if (!enabled) {
      return { show: true, tip: "Save as Draft is disabled for this form profile" };
    }

    return { show: false, tip: null };
  }, [enabled]);

  // Determine if the "Save and Request" button should be shown
  // eslint-disable-next-line arrow-body-style
  const useSaveAndRequestButton = (referralInEditMode, permissionReferralsEdit) => {
    return React.useMemo(() => {
      // Not in edit mode means we are creating a new referral, so we don't show this SaveAndRequest button
      if (!referralInEditMode) return { show: false, label: null };

      if (!permissionReferralsEdit) return { show: false, label: null };

      return { show: true, label: "Save and Request" };
    }, [referralInEditMode, permissionReferralsEdit]);
  };

  // Determine if the "Add" button should be shown
  // eslint-disable-next-line arrow-body-style
  const useAddButton = (referralInEditMode, permissionReferralsCreate) => {
    return React.useMemo(() => {
      // In edit mode means we are editing an existing referral, so we don't show this Add button
      if (referralInEditMode) return { show: false, label: null };

      if (!permissionReferralsCreate) return { show: false, label: null };

      return { show: true, label: "Add" };
    }, [referralInEditMode, permissionReferralsCreate]);
  };

  const onAddReferral = () => {
    dispatch(openDialog({
      maxWidth: enableStreamlinedAddReferral ? "xl" : "md",
      children: (
        <CreateReferralForm
          orgUnitId={currentReferralOrgUnitId}
          onSucceed={onSucceed}
          referredToSpecialty={currentAssignedToSpecialty}
          isFormStreamlinedAddReferral={isFormStreamlinedAddReferral}
          isFromNewPatient={isFromNewPatient}
          presetPatient={patientDetails}
        />
      ),
    }, enableStreamlinedAddReferral));
  };

  const handleSubmit = ({ referral: data }, { setSubmitting, setErrors }) => {
    let { referralExpiryDate } = data;

    if (referralExpiryDate) {
      referralExpiryDate = isString(data.referralExpiryDate) ? data.referralExpiryDate : moment(data.referralExpiryDate).format("YYYY-MM-DD");
    }

    let referralData = {
      ...data,
      patientId: patientDetails?.patientId,
      patient: patientDetails,
      nok: !every(data.nok, x => (x === null || x === undefined)) ? { ...data.nok, relationshipType: "NextOfKin" } : null,
      referredToServiceDescription: data.referredToSpecialty?.label,
      assignToSpecialty: data.referredToSpecialty?.value,
      referralSourceDocumentFileId: previewSourceDocument?.id,
      referrerTimeZone: moment.tz.guess(),
      referralSourceDocumentFileName: previewSourceDocument?.fileName,
      externalSourceDocumentId: previewSourceDocument?.externalSourceDocumentId,
      assignToSubSpecialty: data.assignToSubSpecialty?.value ?? data.assignToSubSpecialty,
      typeOfReferralCodeId: data.typeOfReferralCodeId?.value ?? data.typeOfReferralCodeId,
      referrerAdvisedPriorityCodeId: data.referrerAdvisedPriorityCodeId?.value ?? data.referrerAdvisedPriorityCodeId,
      referringSpecialtyCodeId: data.referringSpecialtyCodeId?.value ?? data.referringSpecialtyCodeId,
      referringLocationCodeId: data.referringLocationCodeId?.value ?? data.referringLocationCodeId,
      referringInternalLocationCodeId: data.referringInternalLocationCodeId?.value ?? data.referringInternalLocationCodeId,
      reasonForReferralCodeId: data.reasonForReferralCodeId?.value ?? data.reasonForReferralCodeId,
      claimTypeCodeId: data.claimTypeCodeId?.value ?? data.claimTypeCodeId,
      clinicTypeCodeId: data.clinicTypeCodeId?.value ?? data.clinicTypeCodeId,
      referrerFullName: data.referrerFullName?.label ?? data.referrerFullName,
      designationCodeId: data.designationCodeId?.value ?? data.designationCodeId,
      referralExpiryDate,
      saveAsDraft,
      saveAsProcess,
      externalTriageStatusCodeId: data.externalTriageStatusCodeId?.value ?? data.externalTriageStatusCodeId,
      referralFormProfileId: data.referralFormProfileId?.value ?? data.referralFormProfileId,
    };

    if (!isEmpty(data.presentingComplaintCodes)) {
      const valueArray = multiplePresentingComplaint ? data.presentingComplaintCodes : [].concat(data.presentingComplaintCodes);
      referralData.presentingComplaintCodes = map(valueArray, x => ({
        code: x.value,
        description: x.label,
      }));
    }

    if (presetPatient) {
      referralData = {
        ...referralData,
        patient: {
          ...presetPatient,
          sex: presetPatient.sex?.value,
        },
        primaryPatientIdentifier: presetPatient.primaryPatientIdentifier
                                  ?? find(presetPatient.patientIdentifiers, id => id.patientIdentifierTypeCode === primaryPatientIdentifier)?.identifier,
      };
    }

    referralData = {
      ...referralData,
      patient: !isEmpty(referralData.patient) ? normalizePatient(referralData.patient) : null,
      referralSourceDocument: null,
      activities: null,
    };

    let apiCall;
    if (saveAsDraft) {
      apiCall = draftManualReferral;
    } else if (isEdit) {
      apiCall = editManualReferral;
    } else {
      apiCall = createManualReferral;
    }

    dispatch(apiCall(orgUnitId, referralData)).then(response => {
      setSubmitting(false);
      const showNotification = response.payload.referral?.displayCreatedNotification && (isEmpty(currentReferral) || (currentReferral.referralStatus === "Draft" && !saveAsDraft && !saveAsProcess));

      if (response.error !== true) {
        dispatch(resetPatientSearch());
        dispatch(resetCurrentAttachment());
        dispatch(clearUploadAttachment());

        const { referral } = response.payload;
        if (orgUnitId !== referral.orgUnitId) {
          history.push({ pathname: getDefaultRoute(), search: "", state: { forceLoad: true, referralId: null } });
          onCancel();
        } else {
          onSucceed(response.payload);
        }
        if (showNotification) {
          const referredToSiteName = referral.organisationUnit?.name || "";
          const assignedToDisplayName = referral.assignedTo?.assignedToDisplayName || "";
          // If both values are truthy: ["Site A", "DisplayName B"] becomes "Site A, DisplayName B"
          // If only one value is truthy: ["Site A", ""] becomes ["Site A"] after filtering, then "Site A" after joining
          // If both are falsy: ["", ""] becomes [] after filtering, then "" (an empty string) after joining
          const sentToLabels = [referredToSiteName, assignedToDisplayName].filter(Boolean).join(", ");

          const message = saveAsDraft
            ? `${referral.referralNumber} Saved as Draft`
            : `${referral.referralNumber} created and sent to ${sentToLabels}`;

          dispatch(showMessage({
            variant: "success",
            message,
          }));
        }
      } else {
        setErrors(response.payload);
      }
    });
  };

  let { referral } = schema ? schema?.cast() : {};

  referral = {
    ...referral,
    nok: patientDetails?.nextOfKin ?? {},
    interpreterRequired: patientDetails?.interpreterRequired ?? null,
    preferredLanguage: patientDetails?.primaryLanguage,
    referralSourceDocument: sourceDocument ?? {},
    referredToSpecialty,
    orgUnitId,
    organisationUnit: referral?.organisationUnit ?? orgUnit,
  };

  if (!isEmpty(currentReferral)) {
    const { organisationUnit, patient, referralDateUtc, referralPeriod,
      reasonNote, presentingComplaintNote, relevantPatientHistoryNote,
      interpreterRequired, preferredLanguage, referrerFullName, referringOrgUnitId, referrerOrganisationName,
      id, referralDocumentId, referrerPracticeProviderGuid, referrerPracticeProviderRole, referrerProviderNumber, referrerPhone, referralAssignedToSubSpecialtyId,
      referralAssignedToSubSpecialtyName, presentingComplaints, referrerUrgency } = currentReferral;

    referral = {
      ...referral,
      referralId: id,
      referralDocumentId,
      referralDateUtc,
      referralPeriod,
      reasonNote,
      presentingComplaintNote,
      relevantPatientHistoryNote,
      interpreterRequired,
      preferredLanguage,
      referrerFullName: referrerFullName ? { value: null, label: referrerFullName } : null,
      referringOrgUnitId,
      referringOrgUnit: currentReferral.referringOrgUnit,
      referrerOrganisationName,
      referrerProviderNumber,
      referrerPracticeProviderGuid,
      referrerPracticeProviderRole,
      referrerPhone,
      patientId: patient?.patientId,
      referralSourceDocument: currentReferral.referralSourceDocument,
      assignToSubSpecialty: referralAssignedToSubSpecialtyId ? { value: referralAssignedToSubSpecialtyId, label: referralAssignedToSubSpecialtyName } : null,
      typeOfReferralCodeId: currentReferral.referralDocument.typeOfReferralCodeId,
      mbsConsent: currentReferral.referralDocument.mbsConsent,
      referrerAdvisedPriorityCodeId: currentReferral.referralDocument.referrerAdvisedPriorityCodeId,
      referringSpecialtyCodeId: currentReferral.referralDocument.referringSpecialtyCodeId,
      referringLocationCodeId: currentReferral.referralDocument.referringLocationCodeId,
      referringInternalLocationCodeId: currentReferral.referralDocument.referringInternalLocationCodeId,
      reasonForReferralCodeId: currentReferral.referralDocument.reasonForReferralCodeId,
      clinicTypeCodeId: currentReferral.referralDocument.clinicTypeCodeId,
      claimTypeCodeId: currentReferral.referralDocument.claimTypeCodeId,
      referralExpiryDate: currentReferral.referralDocument.referralExpiryDate,
      designationCodeId: currentReferral.designationCodeId,
      designationDisplayValue: currentReferral.designationDisplayValue,
      assignedToSpecialist: (currentReferral.referralDocument.specialistConsultantUserId || currentReferral.referralDocument.specialistConsultant)
        ? {
          value: currentReferral.referralDocument.specialistConsultantUserId,
          label: currentReferral.referralDocument.specialistConsultant,
        } : null,
      specialistConsultant: currentReferral.referralDocument.specialistConsultant,
      specialistConsultantUserId: currentReferral.referralDocument.specialistConsultantUserId,
      meetsRequirements: currentReferral.referralDocument.meetsRequirements,
      referrerUrgency,
      isWardRequest: currentReferral.isWardRequest,
      isAcknowledged: currentReferral.isAcknowledged,
      referralFormProfileId: currentReferral.referralFormProfileId,
      formFieldConfigurationId: currentReferral.formFieldConfigurationId,
      orgUnitId: currentReferral.orgUnitId,
      organisationUnit,
    };

    if (presentingComplaints) {
      if (multiplePresentingComplaint) {
        referral.presentingComplaintCodes = map(presentingComplaints, presentingComplaint => ({
          value: presentingComplaint.code,
          label: presentingComplaint.term,
        }));
      } else {
        const presentingComplaint = head(presentingComplaints);
        referral.presentingComplaintCodes = !isEmpty(presentingComplaint)
          ? {
            value: presentingComplaint?.code,
            label: presentingComplaint?.term,
          } : null;
      }
    }
  }

  // The refererFullName sent from backend is a string, and it is widely used in the front-end. So we'd better keep the
  // data source (backend) as string and only convert it to object here.
  //
  // Why we need this conversion?
  //  - The Autocomplete component used in ProviderSelectorInput (referrer-information-section) requires referrerFullName
  //    to be an object with {value, label} structure to function properly. Also, the Yup validation schema (referral-schema.js)
  //    explicitly defines referrerFullName as an object, not a string.
  //
  // Why we need `if (!referral.referrerFullName)` condition?
  //  - Without this condition, the conversion would run every time templates change (e.g., when switching referral specialties).
  //    This would "double-wrap" already converted objects, creating nested objects like:
  //    {value: {value: "x", label: "x"}, label: {value: "x", label: "x"}}
  if (!referral.referrerFullName) {
    // eslint-disable-next-line no-param-reassign
    templateReferral = {
      ...templateReferral,
      referrerFullName: !isEmpty(templateReferral?.referrerFullName)
        ? { value: templateReferral.referrerFullName, label: templateReferral.referrerFullName }
        : null,
    };
  }

  if (!isEmpty(templateReferral)) {
    referral = {
      ...referral,
      ...templateReferral,
    };
  }

  const saveAsDraftButton = useSaveAsDraftButton(
    enableDraft,
    allowSaveAsDraft,
    currentReferral?.referralStatus,
    hasPermissionReferralsDraft,
  );

  const tipSaveAsDraftButton = useTipSaveAsDraftButton(enableDraft);

  const renderExtraActions = (referralStatus, formProps) => (
    <div className="flex-row-container with-gutter ml-16">
      {enableBack
        && (
          <>
            <DefaultButton
              icon={<Icon>arrow_back</Icon>}
              label="Back"
              color="default"
              variant="text"
              onClick={() => {
                const touchedFields = formProps.touched;
                const hasChanged = !isEqual(formProps.initialValues, formProps.values);
                if (touchedFields && hasChanged) {
                  setIsOpenConfirm(true);
                } else {
                  onAddReferral();
                }
              }}
            />
            <BackReferralConfirmDialog open={isOpenConfirm} onConfirm={onAddReferral} onCancel={() => setIsOpenConfirm(false)} />
          </>

        )}
      {saveAsDraftButton.show
        && (
        <SubmitButton
          label="Save as draft"
          onClick={() => setSaveAsDraft(true)}
          {...formProps}
        />
        )}
      {tipSaveAsDraftButton.show
        && (
          <Tooltip title={tipSaveAsDraftButton.tip} arrow>
            <span>
              <DefaultButton
                label="Save as draft"
                disabled
              />
            </span>
          </Tooltip>
        )}
      {(referralStatus === "Processing")
        && (
        <SubmitButton
          label="Save as Process"
          onClick={() => setSaveAsProcess(true)}
          {...formProps}
        />
        )}
    </div>
  );

  const saveAndRequestButton = useSaveAndRequestButton(isEdit, hasPermissionReferralsEdit);
  const addButton = useAddButton(isEdit, hasPermissionReferralsCreate);

  const renderSubmitButton = formProps => {
    if (saveAndRequestButton.show) {
      return (
        <SubmitButton
          label={saveAndRequestButton.label}
          onClick={() => { setSaveAsDraft(false); setSaveAsProcess(false); }}
          {...formProps}
        />
      );
    }

    if (addButton.show) {
      return (
        <SubmitButton
          label={addButton.label}
          onClick={() => { setSaveAsDraft(false); setSaveAsProcess(false); }}
          {...formProps}
        />
      );
    }

    // Return hidden button instead of null to prevent fallback DefaultButton (DefaultButton logic in components\form.js)
    return (
      <div style={{ display: "none" }}>
        <SubmitButton {...formProps} />
      </div>
    );
  };

  const onFileUploaded = uploadedSourceDocumentFile => {
    if (uploadedSourceDocumentFile) {
      setPreviewSourceDocument(uploadedSourceDocumentFile);
    }
  };

  const onFileRemove = () => {
    setPreviewSourceDocument(null);
    setIsNewUpload(true);
  };

  const handleCancel = () => {
    dispatch(resetCurrentAttachment());
    dispatch(resetPatientSearch());
    dispatch(clearUploadAttachment());
    if (onCancel) {
      onCancel();
    }
  };

  const fileData = (previewSourceDocument?.fileData || previewSourceDocument?.base64File || previewSourceDocument?.htmlPreview);
  const hasPreview = !isEmpty(fileData);

  const renderSourceDocumentSection = props => {
    // 1. if a Referral Source Document is uploaded (so this situation has to be Editing a Referral), render the preview component
    // 2. if the disableSourceDocumentUpload set to false, render the preview component
    if ((isEdit && hasPreview) || !disableSourceDocumentUpload) {
      return (
        <ReferralSourceDocumentPreview
          fileData={fileData}
          mimeType={previewSourceDocument?.mimeType}
          pdfProps={{ paged: true, showPreviewControls: true, ...props }}
          hasPreview
          loading={isReferralSourceDocumentFileLoading}
          orgUnitId={currentReferralOrgUnitId}
          specialtyId={currentAssignedToSpecialty?.value}
          label="Drag & drop your pdf source referral here or click on the box to upload"
          onFileUploaded={onFileUploaded}
          disabled={isReading}
          maxFileSizeBytes={totalAttachmentSizeLimitBytes}
        />
      );
    }

    // 3. if the disableSourceDocumentUpload set to true, render the static message
    return (
      <ReferralSourceDocumentDisableUpload helperText={referralSourceDocumentUploadDisabledMarkdown} showIcon={showReferralSourceDocumentUploadDisableIcon} />
    );
  };

  useEffect(() => {
    // Early exit: If acknowledgement feature is disabled in field settings,
    // hide the section and stop further processing
    // Execution order:
    // 1. fieldSettings?.acknowledgement - safely accesses property (returns undefined if fieldSettings is null/undefined)
    // 2. ! - applies logical NOT to the result
    if (!fieldSettings?.acknowledgement) {
      setShowAcknowledgementSection(false);
      return;
    }

    // if we are editing a referral, only show the acknowledgement section if the field setting is enabled and
    // the referral is acknowledged
    //
    // if we are creating a new referral, the acknowledgement section if the field setting is enabled
    const shouldShow = isEdit
      ? fieldSettings.acknowledgement && referral?.isAcknowledged
      : fieldSettings.acknowledgement;

    setShowAcknowledgementSection(shouldShow);
  }, [isEdit, fieldSettings?.acknowledgement, referral?.isAcknowledged]);

  return (
    <ReferralFormComponent
      renderPreview={props => (
        <div className={classes.rootContainer} elevation={1}>
          <div className={classes.headerContainer}>
            {(isEdit) && hasPreview && (
              <IconButton style={{ marginLeft: "4px" }} title="Remove file" icon="delete" disabled={isReading} edge="end" onClick={onFileRemove} className="pr-8" />
            )}
          </div>
          <div className={classes.documentContainer}>
            {renderSourceDocumentSection(props)}
          </div>
        </div>
      )}
    >
      <Form
        onCancel={handleCancel}
        contentProps={{ ...other, submitLabel: isEdit ? "Save" : "Add" }}
        initialValues={{ referral }}
        onSubmit={handleSubmit}
        validationSchema={schema}
        renderHeaderContent={<PatientSummaryCard patient={patientDetails} />}
        renderExtraActions={props => renderExtraActions(currentReferral?.referralStatus, props)}
        renderSubmitButton={renderSubmitButton}
        variant="filled"
        content={({ values, setFieldValue }) => {
          const calculateReferralExpiryDate = (typeOfReferralCode, referralDateUtc) => {
            // If the type of referral has a duration setting, update the expiry to the new referral date + setting months
            const typeOfReferralCodeId = isObject(typeOfReferralCode)
              ? typeOfReferralCode?.value
              : typeOfReferralCode;
            const months = codeSetValueProperties(typeOfReferralCodeId)?.referralDurationMonths;
            if (months) {
              setFieldValue("referral.referralExpiryDate", moment(referralDateUtc).add(months, "M"));
            }
          };

          const referralDetailComponents = sectionComponentProps => {
            if (loadingFieldSettings) return <LoadingState />;
            return (
              <>
                <Field
                  name="referral.referrerInformationSection"
                  component={ReferrerInformationSection}
                  calculateReferralExpiryDate={calculateReferralExpiryDate}
                  {...sectionComponentProps}
                />
                <Field
                  name="referral.referralInformationSection"
                  component={ReferralInformationSection}
                  {...sectionComponentProps}
                  isEdit={isEdit}
                  currentReferral={currentReferral}
                  calculateReferralExpiryDate={calculateReferralExpiryDate}
                  currentAssignedToSpecialty={currentAssignedToSpecialty}
                  presentingComplaintMaxLength={presentingComplaintMaxLength}
                  patientPresentingComplaints={patientPresentingComplaints}
                  isEmptyPatient={isEmptyPatient}
                />
                <Field
                  name="referral.billingInformationSection"
                  component={BillingInformationSection}
                  fieldSettings={sectionComponentProps?.fieldSettings}
                  fieldLabels={sectionComponentProps?.fieldLabels}
                  referral={sectionComponentProps?.referral}
                  calculateReferralExpiryDate={calculateReferralExpiryDate}
                  currentAssignedToSpecialty={currentAssignedToSpecialty}
                  referralFormLayoutSetting={referralFormLayoutSetting}
                  allowSpecialtyReassignment={allowSpecialtyReassignment}
                />
                {fieldSettings?.attachments && (
                <Section withDivider title={fieldLabels.attachments}>
                  <Field
                    name="referral.addedAttachments"
                    label="Drag and drop files here, or click to select"
                    component={ReferralDocumentAttachmentUpload}
                    orgUnitId={orgUnit.id}
                    disabled
                    referralId={currentReferral?.id}
                    referralPermissions={currentReferral?.permissions}
                    removedAttachmentFieldName="referral.removedAttachments"
                  />
                </Section>
                )}
                {showAcknowledgementSection && (
                  <Field
                    name="referral.acknowledgementSection"
                    component={AcknowledgementSection}
                    fieldSettings={sectionComponentProps?.fieldSettings}
                    isEdit={isEdit}
                    readOnlyInEditMode
                  />
                )}
              </>
            );
          };

          return (
            <>
              <ReferralWarningMessage warningMessages={warningMessages} />
              {renderTemplateComponents && renderTemplateComponents({ isReading, values, loadingFieldSettings, fieldSettings })}
              {(isEmpty(values?.referral?.referralFormProfileId)
               || isEmpty(currentAssignedToSpecialty?.value))
                ? <EmptyState title={`Select a ${specialtyLabel} and ${specialtyFormProfileLabel} to continue`} />
                : referralDetailComponents({ fieldSettings, fieldLabels, isNewUpload, referral: values?.referral, orgUnitId: currentReferralOrgUnitId })}
            </>
          );
        }}
      />
    </ReferralFormComponent>
  );
};

export default withPermissions(
  "ReferralsCreate",
  "ReferralsDraft",
  "ReferralsEdit",
)(CreateReferralFormComponent);
